import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { IActivation } from '../interfaces/activation.interface';

/**
 * Servicio responsable de gestionar la lógica relacionada con la activación.
 *
 * Este servicio está disponible a nivel de aplicación gracias al decorador `@Injectable`
 * con `providedIn: 'root'`, lo que significa que Angular lo proporciona como singleton
 * en toda la aplicación.
 */
@Injectable({
  providedIn: 'root',
})
export class ActivationService {
  /**
   * URL del endpoint para la activación.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP
   * para realizar el proceso de activación en la aplicación.
   */
  private url: string = '/activations';

  /**
   * Constructor de la clase ActivationService.
   *
   * Inyecta el servicio `HttpClient`. El servicio `HttpClient` se
   * utiliza para realizar solicitudes HTTP al servidor de autenticación.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Obtiene todas las activaciones.
   *
   * Esta función realiza una petición para recuperar todas las activaciones disponibles
   * desde el servicio correspondiente. Devuelve una promesa que se resuelve con la
   * lista de activaciones obtenidas.
   *
   * @returns Una promesa que resuelve los datos de las activaciones obtenidas.
   * El tipo `any` representa el tipo de datos, que pueden variar dependiendo de la implementación.
   */
  getAll(): Promise<any> {
    return firstValueFrom(this.http.get(environment.apiUrl + this.url));
  }

  /**
   * Realiza el proceso de añadir una activación.
   *
   * Envía una solicitud POST con los datos de la activación al servidor de autenticación.
   *
   * @param activation - El objeto activation que contiene los datos de la activación.
   *
   * @returns Una promesa que devuelve los datos de la activación.
   */
  create(activation: IActivation): Promise<any> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + this.url, {
        licenseId: activation.license.id,
        hostId: activation.host.id,
        reference: activation.customer.reference,
        nif: activation.customer.nif,
        name: activation.customer.name,
        tradeName: activation.customer.tradeName,
        telephone: activation.customer.telephone,
        address: activation.customer.address,
        city: activation.customer.city,
        province: activation.customer.province,
        postalCode: activation.customer.postalCode,
        country: activation.customer.country,
        code: activation.code,
        expirationType: activation.expirationType,
        expirationDays: activation.expirationDays,
        comment: activation.comment,
        type: activation.type,
      })
    );
  }

  /**
   * Actualiza la información de una activación específica.
   *
   * @param activation - Objeto que contiene los datos de la activación a actualizar.
   *
   * @returns Una promesa que resuelve con el resultado de la actualización.
   */
  update(activation: IActivation): Promise<any> {
    return firstValueFrom(
      this.http.put(`${environment.apiUrl + this.url}/${activation.id}`, {
        sent: activation.sent,
      })
    );
  }
}
